import {
  AccessMatrixRow,
  ActivityCatalogItem,
  CatalogPosition,
  OperatingSystem,
  NewHire,
  SetupPhase,
  SetupStep,
  EquipmentAsset,
  OnboardingDoc,
  Opportunity,
  OppStage,
  PacketReference,
  Persona,
  PersonaLadder,
  PersonRow,
  Pathway,
  ProjectAttributes,
  Project,
  Role,
  Subcontractor,
  TimeOffRequest,
  TrainingData,
  UserAccount,
} from '../models';

/**
 * The single seam between the UI/state and where data lives.
 *
 * Today the concrete implementation is {@link LocalStorageRepository} (browser
 * persistence, no backend). When a real backend is introduced, provide an
 * `ApiRepository` implementing this same contract and change one line in
 * `app.config.ts`. Nothing in the components or the NgRx layer changes.
 *
 * Methods are async (Promise-returning) so the contract already matches an
 * HTTP-backed implementation.
 */
export abstract class DataRepository {

  /** Personas ("View As" identities). Read-only reference data. */
  abstract listPersonas(): Promise<Persona[]>;

  /** Operating Systems (the "gem" areas). Read-only reference data. */
  abstract listOperatingSystems(): Promise<OperatingSystem[]>;

  /** Projects, including generated work sessions + time entries. Persisted. */
  abstract listProjects(): Promise<Project[]>;
  abstract saveProjects(projects: readonly Project[]): Promise<Project[]>;


  /** Career-pathway pathways (tracks + tier levels). Read-only reference data. */
  abstract listPositionPathways(): Promise<Pathway[]>;
  /** Per-persona ladder placement (which pathway + tier). Read-only in this phase. */
  abstract listPersonaLadders(): Promise<PersonaLadder[]>;

  /** Our Team directory rows (pre-joined). Read-only in this phase. */




  /** Persist the equipment inventory (interim: whole-list write, like saveDivisions). */
  abstract saveEquipmentAssets(assets: readonly EquipmentAsset[]): Promise<EquipmentAsset[]>;

  /** The authored Get Started checklist (see SetupStep). */
  abstract listSetupSteps(): Promise<SetupStep[]>;

  /** The groups those steps are organised into. */
  abstract listSetupPhases(): Promise<SetupPhase[]>;

  /** User accounts. Admin edits are session-only in this phase. */
  abstract listUsers(): Promise<UserAccount[]>;
  /** Role definitions. */
  abstract listRoles(): Promise<Role[]>;
  /** Resource×role access matrix. */
  abstract listAccessMatrix(): Promise<AccessMatrixRow[]>;
  /** Persist the access matrix (edited in Users & Access). */
  abstract saveAccessMatrix(rows: readonly AccessMatrixRow[]): Promise<AccessMatrixRow[]>;
  /** Catalog positions (for position assignment). */
  abstract listPositionCatalog(): Promise<CatalogPosition[]>;
  /** Persist the position catalog (positions created/edited in the Positions screen). */
  abstract savePositionCatalog(catalog: readonly CatalogPosition[]): Promise<CatalogPosition[]>;

  /** People directory rows (persona + account + HR detail). Session-only edits. */
  abstract listPeople(): Promise<PersonRow[]>;

  /** DRTCS training data (framework, assignments, completions). Session-only edits. */
  abstract listTraining(): Promise<TrainingData>;


  /** Required onboarding documents. */
  abstract listOnboardingDocs(): Promise<OnboardingDoc[]>;
  /** New hires in the onboarding pipeline. Signatures are session-only. */
  abstract listNewHires(): Promise<NewHire[]>;

  /** Position Packet reference data (cover memos, policies, pillars, training, growth). */
  abstract listPacketReference(): Promise<PacketReference>;

  /** BD funnel opportunities. */
  abstract listOpportunities(): Promise<Opportunity[]>;
  /** Funnel stages (Lead → Qualified → Bid → Awarded / Lost). */
  abstract listOppStages(): Promise<OppStage[]>;

  /** Managed project attribute lists (types, contracts, states, size tiers, …). */
  abstract listProjectAttributes(): Promise<ProjectAttributes>;
  /** CPM activity catalog. */
  abstract listActivityCatalog(): Promise<ActivityCatalogItem[]>;

  /** Weekly Schedule ("Perfect Week") reference data. */

  /** Labor-pool subcontractors. */
  abstract listSubcontractors(): Promise<Subcontractor[]>;

  /** Field time-off requests. */
  abstract listTimeOff(): Promise<TimeOffRequest[]>;
}
